import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { BellIcon, XMarkIcon, EyeIcon, TrashIcon } from '@heroicons/react/24/outline';
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/solid';
import { loadNotifications } from '../store/slices/notificationSlice';
import {
  useGetMyNotificationsQuery,
  useMarkNotificationReadMutation,
  useMarkAllNotificationsReadMutation,
  useClearAllNotificationsMutation,
  useDeleteNotificationMutation,
} from '../services/notificationApi';
import { formatDateTimeDDMMYYYY } from '../utils/dateUtils';

const NotificationItem = ({ notification, onDismiss }) => {
  const getIcon = (type) => {
    switch (type) {
      case 'success':
        return <CheckCircleIcon className="h-5 w-5 text-green-500" />;
      case 'warning':
        return <ExclamationTriangleIcon className="h-5 w-5 text-yellow-500" />;
      case 'error':
        return <XMarkIcon className="h-5 w-5 text-red-500" />;
      default:
        return <InformationCircleIcon className="h-5 w-5 text-blue-500" />;
    }
  };

  const getBgColor = (type) => {
    switch (type) {
      case 'success':
        return 'bg-green-50 border-green-200';
      case 'warning':
        return 'bg-yellow-50 border-yellow-200';
      case 'error':
        return 'bg-red-50 border-red-200';
      default:
        return 'bg-blue-50 border-blue-200';
    }
  };

  return (
    <div
      className={`p-4 rounded-xl border-2 ${getBgColor(
        notification.type
      )} shadow-md hover:shadow-lg transition-all duration-200`}
    >
      <div className="flex items-start">
        <div className="flex-shrink-0">
          <div
            className={`p-1.5 rounded-lg ${
              notification.type === 'success'
                ? 'bg-green-100'
                : notification.type === 'warning'
                ? 'bg-yellow-100'
                : notification.type === 'error'
                ? 'bg-red-100'
                : 'bg-blue-100'
            }`}
          >
            {getIcon(notification.type)}
          </div>
        </div>
        <div className="ml-4 flex-1 min-w-0">
          <p className="text-sm font-semibold text-gray-900 leading-tight">
            {notification.title}
          </p>
          <p className="mt-1 text-sm text-gray-700 leading-relaxed line-clamp-2">
            {notification.message}
          </p>
          <p className="mt-3 text-xs font-medium text-gray-500 flex items-center">
            <svg
              className="h-3 w-3 mr-1"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            {formatDateTimeDDMMYYYY(notification.timestamp)}
          </p>
        </div>
        <div className="ml-3 flex-shrink-0">
          <button
            onClick={(e) => {
              e.stopPropagation();
              onDismiss(notification.id);
            }}
            className="inline-flex items-center justify-center w-8 h-8 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all duration-200"
            title="Dismiss notification"
          >
            <XMarkIcon className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

const NotificationDropdown = ({ isOpen, onClose, onBadgeUpdate }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { data, refetch, isFetching } = useGetMyNotificationsQuery({
    page: 1,
    limit: 20,
    unreadOnly: true,
  });
  const [markRead] = useMarkNotificationReadMutation();
  const [markAllReadApi] = useMarkAllNotificationsReadMutation();
  const [clearAllApi] = useClearAllNotificationsMutation();
  const [deleteNotification] = useDeleteNotificationMutation();

  const notifications = (data?.data?.notifications || []).map((n) => ({
    id: n._id,
    type: n.type,
    title: n.title,
    message: n.message,
    timestamp: n.createdAt,
    read: n.read,
    event: n.event,
    data: n.data,
  }));
  const unreadCount = notifications.filter((n) => !n.read).length;

  const handleNotificationClick = async (notification) => {
    if (!notification.read) {
      await markNotificationAsRead(notification.id);
    }

    if (notification.event) {
      if (notification.event.startsWith('leave:')) {
        navigate('/leaves');
      } else if (notification.event.startsWith('loan:')) {
        navigate('/loans');
      } else if (notification.event.startsWith('attendance:')) {
        navigate('/attendance');
      } else if (notification.event.startsWith('holiday:')) {
        navigate('/holidays');
      } else if (notification.event.startsWith('shift:')) {
        navigate('/shifts');
      } else if (notification.event.startsWith('payroll:')) {
        navigate('/payroll');
      } else if (notification.event.startsWith('subscription:')) {
        navigate('/subscription');
      }
    }

    onClose?.();
  };

  useEffect(() => {
    dispatch(loadNotifications());
  }, [dispatch]);

  const dismissNotification = async (id) => {
    try {
      await deleteNotification(id).unwrap();
      refetch();
      onBadgeUpdate?.();
    } catch (error) {
      console.error('Failed to delete notification:', error);
    }
  };

  const markNotificationAsRead = async (id) => {
    try {
      await markRead(id).unwrap();
      refetch();
      onBadgeUpdate?.();
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  };

  const clearAllNotifications = async () => {
    try {
      await clearAllApi().unwrap();
      refetch();
      onBadgeUpdate?.();
    } catch (error) {
      console.error('Failed to clear notifications:', error);
    }
  };

  const markAllNotificationsAsRead = async () => {
    try {
      await markAllReadApi().unwrap();
      refetch();
      onBadgeUpdate?.();
    } catch (error) {
      console.error('Failed to mark all notifications as read:', error);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Mobile backdrop */}
      <div
        className="fixed inset-0 z-40 bg-black/30 sm:hidden"
        onClick={onClose}
      />

      {/* Popup container:
          - mobile: fixed with margin from edges (like a dialog)
          - desktop: absolute under the bell (same as before)
      */}
      <div
        className="
          fixed sm:absolute
          inset-x-3 top-16
          sm:inset-auto sm:right-0 sm:top-full sm:mt-3
          w-[calc(100vw-1.5rem)] max-w-sm
          sm:w-96 sm:max-w-md
          bg-white rounded-xl shadow-2xl ring-1 ring-gray-200 border border-gray-100
          z-50 sm:z-60 overflow-hidden
        "
      >
        {/* Header Section */}
        <div className="border-b border-gray-100 bg-gradient-to-r from-slate-50 to-gray-50">
          <div className="px-4 sm:px-6 py-3">
            <div className="flex items-center space-x-3">
              <div className="relative flex items-center justify-center w-8 h-8 bg-blue-500 rounded-lg shadow-sm">
                <BellIcon className="h-4 w-4 text-white" />
              </div>
              <div className="flex flex-wrap items-center gap-1 sm:space-x-2">
                <h2 className="text-base sm:text-lg font-semibold text-gray-900 tracking-tight">
                  Notifications
                </h2>
                {unreadCount > 0 && (
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
                    <span className="text-xs sm:text-sm font-medium text-red-600">
                      {unreadCount} unread
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="px-4 sm:px-6 pb-3 flex items-center justify-center space-x-2 sm:space-x-3">
            {unreadCount > 0 && (
              <button
                onClick={markAllNotificationsAsRead}
                className="inline-flex items-center space-x-1.5 sm:space-x-2 px-3 sm:px-4 py-2 text-xs sm:text-sm font-semibold text-blue-700 bg-blue-50 hover:bg-blue-100 active:bg-blue-200 border-2 border-blue-200 rounded-xl transition-all duration-200 hover:shadow-md hover:shadow-blue-100/50 transform hover:scale-[1.02] active:scale-[0.98]"
                title="Mark all notifications as read"
              >
                <EyeIcon className="h-4 w-4" />
                <span className="hidden sm:inline font-medium">Mark all as read</span>
                <span className="sm:hidden font-medium">Read all</span>
              </button>
            )}

            <button
              onClick={clearAllNotifications}
              className="inline-flex items-center space-x-1.5 sm:space-x-2 px-3 sm:px-4 py-2 text-xs sm:text-sm font-semibold text-gray-700 bg-gray-50 hover:bg-gray-100 active:bg-gray-200 border-2 border-gray-200 rounded-xl transition-all duration-200 hover:shadow-md hover:shadow-gray-200/50 transform hover:scale-[1.02] active:scale-[0.98]"
              title="Clear all notifications"
            >
              <TrashIcon className="h-4 w-4" />
              <span className="hidden sm:inline font-medium">Clear all</span>
              <span className="sm:hidden font-medium">Clear</span>
            </button>
          </div>
        </div>

        {/* List */}
        <div className="max-h-[65vh] sm:max-h-96 overflow-y-auto">
          {isFetching ? (
            <div className="p-6 sm:p-8 text-center">
              <div className="inline-flex items-center justify-center w-8 h-8">
                <div className="animate-spin rounded-full h-6 w-6 border-2 border-blue-100 border-t-blue-600"></div>
              </div>
              <p className="mt-3 text-sm font-medium text-gray-600">
                Loading notifications...
              </p>
            </div>
          ) : notifications.length === 0 ? (
            <div className="p-8 sm:p-12 text-center">
              <div className="inline-flex items-center justify-center w-14 h-14 sm:w-16 sm:h-16 bg-gradient-to-br from-gray-100 to-gray-200 rounded-full mb-4">
                <BellIcon className="h-7 w-7 sm:h-8 sm:w-8 text-gray-400" />
              </div>
              <h3 className="text-sm sm:text-base font-semibold text-gray-900 mb-1">
                No notifications
              </h3>
              <p className="text-xs sm:text-sm text-gray-500">
                You're all caught up! Check back later for updates.
              </p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {notifications.map((notification) => (
                <div
                  key={notification.id}
                  className={`p-3 sm:p-4 hover:bg-gray-50 cursor-pointer transition-colors duration-200 ${
                    !notification.read ? 'bg-blue-50/70 hover:bg-blue-100/50' : ''
                  }`}
                  onClick={() => handleNotificationClick(notification)}
                >
                  <NotificationItem
                    notification={notification}
                    onDismiss={dismissNotification}
                  />
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-4 py-3 border-t border-gray-100 bg-gradient-to-r from-gray-50 to-white rounded-b-xl">
          <Link
            to="/notifications"
            className="w-full inline-flex items-center justify-center px-4 py-2 text-xs sm:text-sm font-semibold text-blue-600 hover:text-blue-700 hover:bg-blue-50 border border-blue-200 rounded-lg transition-all duration-200 hover:shadow-sm"
            onClick={() => onClose?.()}
          >
            <span>View all notifications</span>
            <svg
              className="ml-2 h-4 w-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </Link>
        </div>
      </div>
    </>
  );
};

const NotificationBell = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { data, refetch } = useGetMyNotificationsQuery({
    page: 1,
    limit: 20,
    unreadOnly: true,
  });
  const serverUnread = (data?.data?.notifications || []).filter((n) => !n.read).length;
  const localUnread = useSelector((state) => state.notifications.unreadCount);
  const unreadCount = serverUnread !== undefined ? serverUnread : localUnread;

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (isOpen && !event.target.closest('.notification-container')) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleOutsideClick);
      return () => document.removeEventListener('mousedown', handleOutsideClick);
    }
  }, [isOpen]);

  return (
    <div className="relative notification-container">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative flex items-center justify-center w-10 h-10 text-gray-600 hover:text-gray-900 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 rounded-xl transition-all duration-200 group"
        aria-label={`Notifications ${unreadCount > 0 ? `(${unreadCount} unread)` : ''}`}
      >
        <BellIcon className="h-5 w-5 group-hover:scale-110 transition-transform duration-200" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 inline-flex items-center justify-center min-w-[20px] h-[20px] px-1 text-xs font-bold leading-none text-white bg-gradient-to-r from-red-500 to-red-600 border-2 border-white rounded-full animate-pulse shadow-lg scale-110">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      <NotificationDropdown
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        onBadgeUpdate={() => refetch()}
      />
    </div>
  );
};

export { NotificationBell, NotificationDropdown, NotificationItem };
